import { useState } from 'react';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  selectIncident,
  selectIncidents,
  useAppSelector,
} from 'slices/commonSelectors';

export function itemMatchesWorkspace(
  layer: { incidentId?: string | null },
  workspaceId: string | null
) {
  if (!workspaceId) return true;
  return layer.incidentId === workspaceId;
}

export function LayerWorkspaceFilterButton({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (workspaceId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const incidents = useAppSelector(selectIncidents);
  const currentIncident = useAppSelector(selectIncident);
  const workspaces =
    currentIncident?.id &&
    !incidents.some((incident) => incident.id === currentIncident.id)
      ? [currentIncident, ...incidents]
      : incidents;

  const choose = (workspaceId: string | null) => {
    onChange(workspaceId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant={value ? 'default' : 'outline'}
            size="icon"
            className="tw:size-8 tw:shrink-0"
            aria-label="Filter by workspace"
            title="Filter by workspace"
            data-tour="hub-cop-layer-presets"
          >
            <Filter className="tw:size-3.5" />
          </Button>
        }
      />
      <PopoverContent align="end" className="dark tw:w-56 tw:gap-1 tw:p-2">
        <p className="tw:mb-1 tw:px-2 tw:text-xs tw:font-medium">Workspace</p>
        <button
          type="button"
          className={cn(
            'tw:w-full tw:rounded-md tw:px-2 tw:py-1.5 tw:text-left tw:text-xs tw:hover:bg-accent',
            !value && 'tw:bg-accent'
          )}
          onClick={() => choose(null)}
        >
          All workspaces
        </button>
        {workspaces.map((workspace) => (
          <button
            key={workspace.id}
            type="button"
            className={cn(
              'tw:w-full tw:truncate tw:rounded-md tw:px-2 tw:py-1.5 tw:text-left tw:text-xs tw:hover:bg-accent',
              value === workspace.id && 'tw:bg-accent'
            )}
            onClick={() => choose(workspace.id)}
          >
            {workspace.name}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
